import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Crown, MapPin, Heart, Sparkles, CheckCircle2, Building2, Filter, Search, Users } from "lucide-react";
import SeoMeta from "@/components/SeoMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const KINDS = [
  { id: "all",      label: "Everyone",  icon: Sparkles },
  { id: "creator",  label: "Creators",  icon: Crown },
  { id: "agent",    label: "Agents",    icon: Users },
  { id: "business", label: "Businesses",icon: Building2 },
];

const SORTS = [
  { id: "points", label: "Top this month" },
  { id: "recent", label: "Newest" },
  { id: "name",   label: "A → Z" },
];

export default function CreatorsDirectoryPage() {
  const [params, setParams] = useSearchParams();
  const [kind, setKind] = useState(params.get("kind") || "all");
  const [city, setCity] = useState(params.get("city") || "");
  const [appliedCity, setAppliedCity] = useState(params.get("city") || "");
  const [sort, setSort] = useState(params.get("sort") || "points");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const q = new URLSearchParams();
    if (kind && kind !== "all") q.set("kind", kind);
    if (appliedCity) q.set("city", appliedCity);
    q.set("sort", sort);
    q.set("limit", "48");
    axios.get(`${API}/onlybest/creators?${q.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ creators: [], total: 0 }); })
      .finally(() => { if (!cancelled) setLoading(false); });

    // Sync URL
    const urlQ = new URLSearchParams();
    if (kind !== "all") urlQ.set("kind", kind);
    if (appliedCity) urlQ.set("city", appliedCity);
    if (sort !== "points") urlQ.set("sort", sort);
    setParams(urlQ, { replace: true });
    return () => { cancelled = true; };
  }, [kind, appliedCity, sort, setParams]);

  return (
    <div className="min-h-screen bg-[#070B16] text-white pt-24 pb-24" data-testid="creators-directory-page">
      <SeoMeta
        title="Meet the OnlyBest Movement — Creators, Agents & Businesses | LocalBest.ai"
        description="Discover the people promoting the best of America — OnlyBest creators, professional agents, and businesses lifting up their communities."
        canonical="https://localbest.ai/creators"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#DC2626]/15 to-[#2563EB]/15 border border-[#DC2626]/30 mb-5">
            <Crown className="w-3.5 h-3.5 text-[#DC2626]" />
            <span className="text-xs sm:text-sm font-semibold text-[#DC2626]">OnlyBest™ Directory</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] leading-[1.05] tracking-tight">
            Meet the <span className="bg-gradient-to-r from-[#DC2626] via-[#2563EB] to-[#1E40AF] bg-clip-text text-transparent">good influencers</span> of America.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#94A3B8] max-w-2xl mx-auto">
            Creators, professionals, and businesses recommending what's worth your trust.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-8" data-testid="creators-filters">
          <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1 flex-wrap">
            {KINDS.map((k) => {
              const Ic = k.icon;
              return (
                <button
                  key={k.id}
                  onClick={() => setKind(k.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs sm:text-sm font-semibold transition ${
                    kind === k.id ? "bg-gradient-to-r from-[#DC2626] to-[#2563EB] text-white" : "text-[#94A3B8] hover:text-white"
                  }`}
                  data-testid={`creators-kind-${k.id}`}
                >
                  <Ic className="w-3.5 h-3.5" /> {k.label}
                </button>
              );
            })}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); setAppliedCity(city.trim()); }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-2 py-1"
          >
            <Search className="w-4 h-4 text-[#94A3B8] ml-1" />
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
              className="bg-transparent text-sm text-white placeholder:text-[#475569] focus:outline-none px-1 py-1.5 w-32"
              data-testid="creators-city-input"
            />
            <button type="submit" className="text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white">Go</button>
          </form>

          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs sm:text-sm text-white focus:outline-none"
            data-testid="creators-sort"
          >
            {SORTS.map((s) => <option key={s.id} value={s.id} className="bg-[#0F172A]">{s.label}</option>)}
          </select>
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-56 rounded-2xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <p className="text-sm text-[#94A3B8] mb-4">{data?.total || 0} active OnlyBest participants{appliedCity ? ` in ${appliedCity}` : ""}.</p>
            {data?.creators?.length === 0 ? (
              <div className="text-center py-20" data-testid="creators-empty">
                <Crown className="w-10 h-10 text-[#DC2626] mx-auto mb-3" />
                <p className="text-white font-medium">No participants match this filter — yet.</p>
                <Link to="/onlybest" className="inline-flex mt-5 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#DC2626] to-[#2563EB] text-white text-sm font-semibold">
                  Be the first in your city
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="creators-grid">
                {data?.creators?.map((c) => <CreatorCard key={c.slug} creator={c} />)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CreatorCard({ creator }) {
  const kindLabel =
    creator.kind === "creator" ? "Creator" :
    creator.kind === "agent" ? "Agent / Pro" : "Business";
  const kindEmoji = creator.kind === "creator" ? "👑" : creator.kind === "agent" ? "🤝" : "🏢";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35 }}
    >
      <Link
        to={`/business/${creator.slug}`}
        className="block rounded-2xl border border-white/10 bg-gradient-to-br from-[#0F172A] to-[#0A0F1C] p-4 hover:border-[#DC2626]/40 hover:shadow-xl hover:shadow-[#DC2626]/10 transition min-w-0"
        data-testid={`creator-card-${creator.slug}`}
      >
        <div className="flex items-start gap-3 min-w-0">
          {creator.logo_url ? (
            <img src={creator.logo_url} alt={creator.name} className="w-14 h-14 rounded-xl object-cover bg-white/10 flex-shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Crown className="w-6 h-6 text-[#DC2626]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold truncate flex items-center gap-1.5">
              {creator.name}
              {creator.is_verified && <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />}
            </p>
            <p className="text-[11px] text-[#94A3B8] truncate">
              {kindEmoji} {kindLabel}
            </p>
            {(creator.city || creator.state) && (
              <p className="text-[10px] text-[#64748B] truncate flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3" /> {[creator.city, creator.state].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        </div>
        {creator.onlybest_tagline && (
          <p className="mt-3 text-xs text-[#CBD5E1] italic line-clamp-2">"{creator.onlybest_tagline}"</p>
        )}
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          <Cell value={creator.picks_count} label="Picks" icon={Sparkles} />
          <Cell value={creator.causes_count} label="Causes" icon={Heart} />
          <Cell value={creator.points_30d || 0} label="Pts/30d" icon={Filter} highlight />
        </div>
      </Link>
    </motion.div>
  );
}

function Cell({ value, label, icon: Icon, highlight }) {
  return (
    <div className={`rounded-lg p-2 ${highlight ? "bg-gradient-to-br from-[#DC2626]/15 to-[#2563EB]/10" : "bg-white/[0.03]"}`}>
      <div className={`text-sm font-bold tabular-nums ${highlight ? "text-[#DC2626]" : "text-white"}`}>{(value ?? 0).toLocaleString()}</div>
      <div className="text-[10px] text-[#94A3B8] uppercase tracking-wider flex items-center justify-center gap-0.5">
        <Icon className="w-2.5 h-2.5" /> {label}
      </div>
    </div>
  );
}
